import { useEffect } from 'react';
import { useBackground } from '~/components/Providers/BackgroundProvider';
import { getFullLevelThumbnail, seo } from '~/util';
import { levelsQuery } from '~/util/queries/public/levels';
import ProfileSummary from './-ProfileSummary';
import ProfileMain from './-ProfileMain';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import {
    defaultProfileSearchParams,
    profileParamsSchema,
} from './-SearchParams';
import { userProfileQuery } from '~/util/queries/public/profiles';
import { packsQuery } from '~/util/queries/public/other';
import { DefaultNotFound } from '~/components/DefaultNotFound';
import { createOGProfileData } from './-og';
import { convertOGDataToPayload } from '~/util/workers';
import { ApiError } from '~/components/Providers/APIClient';
import { useScopes } from '~/components/Providers/AuthProvider';

export const Route = createFileRoute('/profile/user/$userid')({
    validateSearch: profileParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultProfileSearchParams)],
    },
    loaderDeps: ({ search: { list } }) => ({ list }),
    loader: async ({
        context: { queryClient },
        params: { userid },
        deps: { list },
    }) => {
        const [classic, platformer] = await Promise.all([
            queryClient.fetchQuery(
                userProfileQuery({ list: 'classic', userid }),
            ),
            queryClient.fetchQuery(
                userProfileQuery({ list: 'platformer', userid }),
            ),
        ]);
        const classicTotal = (
            classic?.records?.filter(
                (record) =>
                    record.level.status == 'MainList' ||
                    record.level.status == 'Pending',
            ) ?? []
        ).length;

        const platformerTotal = (
            platformer?.records?.filter(
                (record) =>
                    record.level.status == 'MainList' ||
                    record.level.status == 'Pending',
            ) ?? []
        ).length;

        // Prefetch levels for level suggestion
        queryClient.prefetchQuery(levelsQuery({ list: 'classic' }));
        queryClient.prefetchQuery(levelsQuery({ list: 'platformer' }));
        // Prefetch packs for the PacksTableWithThumbnails component
        queryClient.prefetchQuery(packsQuery({ list: 'classic' }));
        queryClient.prefetchQuery(packsQuery({ list: 'platformer' }));

        const profile = list === 'classic' ? classic : platformer;
        const total = list === 'classic' ? classicTotal : platformerTotal;

        return {
            profile,
            total,
            classicTotal,
            platformerTotal,
            encodedOgProfileData: await convertOGDataToPayload(
                createOGProfileData(profile),
            ),
        };
    },
    head: async ({ loaderData }) => {
        const profile =
            (loaderData?.profile?.ban_level ?? 0) < 4
                ? loaderData?.profile
                : undefined;

        return {
            meta: seo({
                title: `${profile?.global_name || 'Unknown User'}'s Profile`,
                description:
                    profile?.description ||
                    "This user hasn't provided a description yet.",
                image: loaderData?.encodedOgProfileData
                    ? import.meta.env.VITE_OG_BASE_URL +
                      `/profile/user?data=${loaderData.encodedOgProfileData}`
                    : undefined,
                imageAlt: profile?.global_name || 'Unknown User',
                fullImage: true,
            }),
        };
    },
    component: Profile,
    notFoundComponent: () =>
        DefaultNotFound({ customNotFound: 'User not found.' }),
});

function Profile() {
    const profile = Route.useLoaderData({ select: ({ profile }) => profile });
    const { setBackground, setOpacity, resetBackground } = useBackground();

    const { hasScope } = useScopes();

    useEffect(() => {
        if (
            profile?.records &&
            profile.records[0]?.level?.level_id &&
            profile.background_level !== undefined
        ) {
            resetBackground();
            setBackground(
                getFullLevelThumbnail(
                    profile.background_level == 0
                        ? profile.records[0].level.level_id
                        : profile.background_level,
                ),
            );
            setOpacity('0.05');
        }
    }, [profile, setBackground, setOpacity, resetBackground]);

    useEffect(() => {
        return resetBackground;
    }, [resetBackground]);

    if (profile.ban_level == 4 && !hasScope(undefined)) {
        throw new ApiError('This user has been redacted.', 403, undefined);
    }

    return (
        <main className="h-full flex justify-center">
            <div className="flex flex-col items-center gap-4 w-full h-full bg-background-dark rounded-2xl py-4">
                <div className="flex flex-col lg:flex-row w-full justify-center items-center lg:items-start lg:justify-start">
                    <ProfileSummary />
                    <ProfileMain />
                </div>
            </div>
        </main>
    );
}
